/**
 * Utility functions for calculating canvas dimensions based on component positions
 * Ensures consistency between editor and viewer modes
 *
 * Enhanced with responsive positioning support for CSS-first architecture
 */

import { Component, Position, Size } from "@/types";
import { ResponsiveBreakpoint } from "@/stores/editor/responsive-store";
import {
  calculateResponsivePosition,
  calculateResponsiveSize,
  applyResponsiveConstraints,
} from "@/utils/responsive-position-calculator";

export interface CanvasDimensions {
  width: number;
  height: number;
}

export interface ViewportDimensions {
  width: number;
  height: number;
}

export interface ResponsiveCanvasDimensions extends CanvasDimensions {
  cssWidth: string;
  cssHeight: string;
  scaleFactor: number;
}

/**
 * Calculate the actual canvas dimensions based on component positions
 * This ensures the canvas expands to accommodate all components
 */
export const calculateCanvasContentBounds = (
  components: Component[],
  viewportDimensions: ViewportDimensions,
  padding: number = 100
): CanvasDimensions => {
  // Start with viewport dimensions as minimum
  let maxWidth = viewportDimensions.width;
  let maxHeight = viewportDimensions.height;

  // If no components, return viewport dimensions
  if (components.length === 0) {
    return { width: maxWidth, height: maxHeight };
  }

  // Calculate the furthest edges of all components
  components.forEach((component) => {
    // Skip components without position (e.g., children in flex containers)
    if (!component.position) {
      return;
    }

    const rightEdge = component.position.x + component.size.width;
    const bottomEdge = component.position.y + component.size.height;

    if (rightEdge > maxWidth) {
      maxWidth = rightEdge;
    }
    if (bottomEdge > maxHeight) {
      maxHeight = bottomEdge;
    }
  });

  // Add padding to ensure components aren't right at the edge
  maxWidth += padding;
  maxHeight += padding;

  // Ensure minimum dimensions are maintained
  return {
    width: Math.max(maxWidth, viewportDimensions.width),
    height: Math.max(maxHeight, viewportDimensions.height),
  };
};

/**
 * Get default viewport dimensions based on viewport type
 */
export const getDefaultViewportDimensions = (viewport: string): ViewportDimensions => {
  switch (viewport) {
    case 'mobile':
      return { width: 375, height: 667 };
    case 'tablet':
      return { width: 768, height: 1024 };
    case 'desktop':
    default:
      return { width: 1920, height: 1080 };
  }
};

/**
 * Calculate responsive canvas dimensions with CSS units
 *
 * Enhanced version that generates both pixel and CSS values for
 * responsive canvas sizing in the new architecture.
 */
interface ResolveResponsivePropertyOptions {
  getResponsiveProperty?: (
    componentId: string,
    propertyPath: string,
    viewportId: string,
  ) => unknown;
}

const safeNumber = (value: unknown, fallback: number): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = parseFloat(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return fallback;
};

export const calculateResponsiveCanvasDimensions = (
  components: Component[],
  viewport: ResponsiveBreakpoint,
  baseViewport: ResponsiveBreakpoint,
  padding: number = 100,
  options: ResolveResponsivePropertyOptions = {}
): ResponsiveCanvasDimensions => {
  // CRITICAL: Validate viewport and baseViewport have valid dimensions
  if (!viewport || !baseViewport) {
    console.error('❌ [calculateResponsiveCanvasDimensions] viewport or baseViewport is undefined!', {
      viewport,
      baseViewport,
    });
    // Return safe default values to prevent NaN
    return {
      width: 1920,
      height: 1080,
      cssWidth: '100%',
      cssHeight: '100%',
      scaleFactor: 1,
    };
  }

  // Validate that width and height are finite numbers
  const safeViewportWidth = Number.isFinite(viewport.width) && viewport.width > 0 ? viewport.width : 1920;
  const safeViewportHeight = Number.isFinite(viewport.height) && viewport.height > 0 ? viewport.height : 1080;
  const safeBaseViewportWidth = Number.isFinite(baseViewport.width) && baseViewport.width > 0 ? baseViewport.width : 1920;
  const safeBaseViewportHeight = Number.isFinite(baseViewport.height) && baseViewport.height > 0 ? baseViewport.height : 1080;

  if (safeViewportWidth !== viewport.width || safeViewportHeight !== viewport.height) {
    console.warn('⚠️ [calculateResponsiveCanvasDimensions] Invalid viewport dimensions detected!', {
      original: { width: viewport.width, height: viewport.height },
      safe: { width: safeViewportWidth, height: safeViewportHeight },
    });
  }

  let maxContentRight = 0;
  let maxContentBottom = 0;

  const resolveResponsiveProperty = options.getResponsiveProperty;

  components.forEach((component) => {
    // Skip components without explicit positioning (likely nested inside responsive parents)
    if (!component.position) {
      return;
    }

    // Skip flex/grid children - they're positioned by their parent container, not absolutely
    if (
      component.responsivePosition?.strategy === 'flex' ||
      component.responsivePosition?.strategy === 'grid' ||
      component.responsivePosition?.strategy === 'relative'
    ) {
      return;
    }

    // Skip components with NaN or string position values (flex children often have these)
    if (
      typeof component.position.x !== 'number' ||
      typeof component.position.y !== 'number' ||
      !Number.isFinite(component.position.x) ||
      !Number.isFinite(component.position.y)
    ) {
      return;
    }

    // Skip components with percentage-based or string size values
    if (
      typeof component.size.width === 'string' ||
      typeof component.size.height === 'string' ||
      !Number.isFinite(component.size.width) ||
      !Number.isFinite(component.size.height)
    ) {
      return;
    }

    const basePosition: Position = {
      x: safeNumber(
        resolveResponsiveProperty?.(component.id, "position.x", viewport.id),
        component.position?.x ?? 0,
      ),
      y: safeNumber(
        resolveResponsiveProperty?.(component.id, "position.y", viewport.id),
        component.position?.y ?? 0,
      ),
      z: safeNumber(
        resolveResponsiveProperty?.(component.id, "position.z", viewport.id),
        component.position?.z ?? 1,
      ),
    };

    const baseSize: Size = {
      width: safeNumber(
        resolveResponsiveProperty?.(component.id, "size.width", viewport.id),
        component.size?.width ?? viewport.width,
      ),
      height: safeNumber(
        resolveResponsiveProperty?.(component.id, "size.height", viewport.id),
        component.size?.height ?? viewport.height,
      ),
    };

    if (baseSize.width <= 0 || baseSize.height <= 0) {
      return;
    }

    const responsivePosition = calculateResponsivePosition({
      basePosition,
      baseViewport,
      targetViewport: viewport,
      containerDimensions: {
        width: safeViewportWidth,
        height: safeViewportHeight,
      },
    });

    const responsiveSize = calculateResponsiveSize(baseSize, baseViewport, viewport);

    const { position, size } = applyResponsiveConstraints(
      responsivePosition.position,
      { width: responsiveSize.width, height: responsiveSize.height },
      viewport,
      {
        minWidth: 1,
        minHeight: 1,
      },
    );

    const rightEdge = position.x + size.width;
    const bottomEdge = position.y + size.height;

    // Validate edges are finite numbers before using them
    if (Number.isFinite(rightEdge) && Number.isFinite(bottomEdge)) {
      maxContentRight = Math.max(maxContentRight, rightEdge);
      maxContentBottom = Math.max(maxContentBottom, bottomEdge);
    } else {
      console.warn('⚠️ [calculateResponsiveCanvasDimensions] Component produced invalid edge values', {
        componentId: component.id,
        rightEdge,
        bottomEdge,
        position,
        size,
      });
    }
  });

  const contentWidth = Math.max(maxContentRight, 0);
  const contentHeight = Math.max(maxContentBottom, 0);

  // Use safe viewport dimensions for padding calculations
  const paddedWidth = (() => {
    if (contentWidth <= safeViewportWidth) {
      return safeViewportWidth;
    }
    return Math.max(safeViewportWidth, contentWidth + padding);
  })();

  const paddedHeight = (() => {
    if (contentHeight <= safeViewportHeight) {
      return safeViewportHeight;
    }
    return Math.max(safeViewportHeight, contentHeight + padding);
  })();

  // Use safe dimensions for ratio calculations to prevent division by zero or NaN
  const widthRatio = safeViewportWidth / safeBaseViewportWidth;
  const heightRatio = safeViewportHeight / safeBaseViewportHeight;
  const scaleFactor = Math.min(widthRatio, heightRatio);

  const widthPercent = (paddedWidth / safeViewportWidth) * 100;
  const heightPercent = (paddedHeight / safeViewportHeight) * 100;

  // FINAL VALIDATION: Ensure we never return NaN values
  const finalWidth = Number.isFinite(paddedWidth) ? Math.round(paddedWidth) : safeViewportWidth;
  const finalHeight = Number.isFinite(paddedHeight) ? Math.round(paddedHeight) : safeViewportHeight;
  const finalScaleFactor = Number.isFinite(scaleFactor) ? scaleFactor : 1;

  if (finalWidth !== Math.round(paddedWidth) || finalHeight !== Math.round(paddedHeight)) {
    console.error('🚨 [calculateResponsiveCanvasDimensions] NaN DETECTED! Using fallback values', {
      paddedWidth,
      paddedHeight,
      finalWidth,
      finalHeight,
      scaleFactor,
      finalScaleFactor,
    });
  }

  return {
    width: finalWidth,
    height: finalHeight,
    cssWidth: `min(${widthPercent}%, ${finalWidth}px)`,
    cssHeight: `min(${heightPercent}%, ${finalHeight}px)`,
    scaleFactor: finalScaleFactor,
  };
};
